import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { BASE_URL } from "../api";
import { getApiHeaders } from "../utils/helpers";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import RewardsJourney from "../component/trade_earn/RewardsJourney";
import ClaimRewardModal from "../component/trade_earn/ClaimRewardModal";

const FALLBACK_IMAGES = [
  "https://images.unsplash.com/photo-1579586337278-3befd40fd17a?auto=format&fit=crop&w=300&q=80",
  "https://images.unsplash.com/photo-1546868871-7041f2a55e12?auto=format&fit=crop&w=300&q=80",
  "https://images.unsplash.com/photo-1592750475338-74b7b21085ab?auto=format&fit=crop&w=300&q=80",
  "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=300&q=80",
  "https://images.unsplash.com/photo-1563720223185-11003d516935?auto=format&fit=crop&w=300&q=80",
];

const getFallbackImage = (item, idx = 0) => {
  if (item?.image_url) return item.image_url;
  const name = String(item?.reward_name || item?.title || "").toLowerCase();
  if (name.includes("pixel") || (name.includes("smartwatch") && name.includes("google"))) {
    return FALLBACK_IMAGES[0];
  }
  if (name.includes("apple") || name.includes("watch")) {
    return FALLBACK_IMAGES[1];
  }
  if (name.includes("iphone") || name.includes("phone")) {
    return FALLBACK_IMAGES[2];
  }
  if (name.includes("dubai") || name.includes("trip")) {
    return FALLBACK_IMAGES[3];
  }
  if (name.includes("rover") || name.includes("car")) {
    return FALLBACK_IMAGES[4];
  }
  return FALLBACK_IMAGES[idx % FALLBACK_IMAGES.length];
};

export default function TradeAndEarn() {
  const [rewardsData, setRewardsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRewardToClaim, setSelectedRewardToClaim] = useState(null);
  const [isClaimModalOpen, setIsClaimModalOpen] = useState(false);

  const fetchTradeEarnList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/deposit/trade_earn_list`, {
        headers: getApiHeaders(),
      });

      if (
        (res.data?.code === 200 || res.data?.status === "success" || res.data?.status === true) &&
        res.data?.data
      ) {
        const raw = res.data.data;
        const list = Array.isArray(raw) ? raw : [raw];
        setRewardsData(list);
      }
    } catch (err) {
      console.error("Error fetching trade_earn_list in TradeAndEarn:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTradeEarnList();
  }, [fetchTradeEarnList]);

  const handleOpenClaimModal = (reward) => {
    setSelectedRewardToClaim(reward);
    setIsClaimModalOpen(true);
  };

  // Prioritize an Unlocked item that is ready to claim, or Pending item, or InProgress item, or default to first item
  const inProgressItem =
    rewardsData.find((item) => {
      const s = String(item.status || "").toLowerCase().replace(/[\s_-]/g, "");
      return s === "unlocked" || s === "unlock";
    }) ||
    rewardsData.find((item) => {
      const s = String(item.status || "").toLowerCase().replace(/[\s_-]/g, "");
      return s === "pending";
    }) ||
    rewardsData.find((item) => {
      const s = String(item.status || "").toLowerCase().replace(/[\s_-]/g, "");
      return s === "inprogress" || s === "active";
    }) ||
    rewardsData[0];

  // LOTS TRADED -- je data aave che ema lots_traded - [0]
  const lotsTraded = Number(
    rewardsData[0]?.lots_traded ?? inProgressItem?.lots_traded ?? 0
  ).toFixed(2);

  // REWARDS WON -- status - Claimed aave eno sum aavshe total
  const rewardsWon = rewardsData.filter((item) => {
    const s = String(item.status || "").toLowerCase();
    return s === "claimed" || s === "completed";
  }).length;

  // NEXT TARGET -- status -InProgress aave eno sum
  const nextTarget = inProgressItem
    ? Number(inProgressItem.lots_required ?? inProgressItem.lots_to_go ?? 0)
    : Number(rewardsData[0]?.lots_required ?? 0);

  const nextRewardItem = inProgressItem || rewardsData[0] || {};
  const nextRewardTitle =
    nextRewardItem.reward_name ||
    "Google | Pixel Watch | Android Smartwatch with Fitbit Activity Tracking";
  const nextRewardImage =
    nextRewardItem.image_url || getFallbackImage(nextRewardItem, 0);
  const nextRewardTargetLots = nextRewardItem.lots_required ?? 50;
  const nextRewardCurrentLots = Number(nextRewardItem.lots_traded ?? 0).toFixed(2);
  const nextRewardLotsToGo = Number(
    nextRewardItem.lots_to_go ??
    Math.max(0, nextRewardTargetLots - Number(nextRewardCurrentLots))
  ).toFixed(2);
  const nextRewardProgress = Number(
    nextRewardItem.progress_percent ??
    (nextRewardTargetLots > 0
      ? Math.round((Number(nextRewardCurrentLots) / nextRewardTargetLots) * 100)
      : 0)
  );

  const nextRewardStatus = String(nextRewardItem.status || "")
    .toLowerCase()
    .replace(/[\s_-]/g, "");
  const isNextRewardPending = nextRewardStatus === "pending";
  const isNextRewardUnlocked =
    !isNextRewardPending &&
    (nextRewardStatus === "unlocked" ||
      nextRewardStatus === "unlock" ||
      (Number(nextRewardTargetLots) > 0 &&
        Number(nextRewardCurrentLots) >= Number(nextRewardTargetLots)));

  return (
    <div id="layout-wrapper">
      <Topbar />
      <Sidebar />

      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid max-w-5xl mb-4">
            <div className="row">
              <div className="col-12">
                <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                  <h2 className="fw-700 mb-sm-0">Trade &amp; Earn</h2>
                </div>
              </div>
            </div>

            <div className="row g-2 g-md-3 mb-3 mb-md-4">
              <div className="col-12 col-sm-4">
                <div className="card card-animate bg-primary-subtle overflow-hidden h-100 mb-0">
                  <div className="card-body upcoming-box p-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <p className="fw-bold font-size-13 font-size-sm-15 text-muted mb-0">Lots Traded</p>
                        <h3 className="mt-1 mt-md-3 ff-secondary font-size-20 font-size-md-22 fw-semibold mb-0">
                          <span className="counter-value">{lotsTraded}</span>
                        </h3>
                      </div>
                      <div className="mini-stat-icon">
                        <div className="avatar-sm upcoming-icon flex-shrink-0">
                          <span className="avatar-title bg-primary rounded-circle fs-2 text-white">
                            <i className="bx bx-trending-up font-size-22" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 col-sm-4">
                <div className="card card-animate bg-success-subtle overflow-hidden h-100 mb-0">
                  <div className="card-body upcoming-box p-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <p className="fw-bold font-size-13 font-size-sm-15 text-muted mb-0">Rewards Won</p>
                        <h3 className="mt-1 mt-md-3 ff-secondary font-size-20 font-size-md-22 fw-semibold mb-0">
                          <span className="counter-value">{rewardsWon}</span>
                        </h3>
                      </div>
                      <div className="mini-stat-icon">
                        <div className="avatar-sm upcoming-icon flex-shrink-0">
                          <span className="avatar-title bg-success rounded-circle fs-2 text-white">
                            <i className="bx bx-trophy font-size-22" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 col-sm-4">
                <div className="card card-animate bg-warning-subtle overflow-hidden h-100 mb-0">
                  <div className="card-body upcoming-box p-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <p className="fw-bold font-size-13 font-size-sm-15 text-muted mb-0">Next Target</p>
                        <h3 className="mt-1 mt-md-3 ff-secondary font-size-20 font-size-md-22 fw-semibold mb-0">
                          <span className="counter-value">{nextTarget}</span>
                        </h3>
                      </div>
                      <div className="mini-stat-icon">
                        <div className="avatar-sm upcoming-icon flex-shrink-0">
                          <span className="avatar-title bg-warning rounded-circle fs-2 text-white">
                            <i className="bx bx-flag font-size-22" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="card shadow-sm mb-3 mb-md-4 card-hover-lift"
              style={{
                borderRadius: "16px",
                backgroundColor: "var(--bs-secondary-bg)",
                border: "1px solid var(--bs-border-color)",
                overflow: "hidden",
              }}
            >
              <div className="card-body p-3 p-md-4">
                <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 gap-md-4">
                  <div className="d-flex flex-column flex-sm-row align-items-start align-items-sm-center gap-3 gap-md-4 flex-grow-1 min-w-0 w-100">
                    <div
                      className="d-flex align-items-start justify-content-start flex-shrink-0 p-1 float-animated"
                      style={{
                        width: "82px",
                        height: "82px",
                        borderRadius: "14px",
                        backgroundColor: "rgba(var(--bs-primary-rgb), 0.08)",
                        border: "1px solid var(--bs-border-color)",
                        overflow: "hidden",
                      }}
                    >
                      <img
                        src={nextRewardImage}
                        alt={nextRewardTitle}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = FALLBACK_IMAGES[0];
                        }}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          borderRadius: "10px",
                        }}
                      />
                    </div>

                    <div className="flex-grow-1 w-100 min-w-0">
                      <div className="mb-2 d-flex flex-wrap align-items-center gap-2">
                        <span
                          className="badge rounded-pill fw-bold font-size-11 px-2.5 py-1 d-inline-flex align-items-center gap-1 shadow-sm"
                          style={{
                            backgroundColor: "rgba(var(--bs-primary-rgb), 0.12)",
                            color: "var(--bs-primary)",
                            border: "1px solid rgba(var(--bs-primary-rgb), 0.2)",
                          }}
                        >
                          <span className="live-dot me-1"></span>
                          <span>NEXT REWARD</span>
                        </span>
                        {isNextRewardPending && (
                          <span
                            className="badge rounded-pill fw-semibold font-size-11 px-2.5 py-1 d-inline-flex align-items-center gap-1 shadow-sm"
                            style={{
                              backgroundColor: "rgba(var(--bs-warning-rgb), 0.15)",
                              color: "#e2a03f",
                              border: "1px solid rgba(var(--bs-warning-rgb), 0.3)",
                            }}
                          >
                            <i className="bx bx-time-five font-size-12"></i>
                            <span>Awaiting approval</span>
                          </span>
                        )}
                      </div>

                      <h5
                        className="fw-bold mb-2 text-wrap"
                        style={{
                          fontSize: "clamp(14px, 3.5vw, 17px)",
                          color: "var(--bs-emphasis-color)",
                          letterSpacing: "-0.2px",
                          lineHeight: "1.4",
                        }}
                      >
                        {nextRewardTitle}
                      </h5>

                      <div className="d-flex align-items-center gap-2 gap-sm-3 mb-2">
                        <div
                          className="progress flex-grow-1"
                          style={{
                            height: "8px",
                            borderRadius: "10px",
                            backgroundColor: "rgba(var(--bs-primary-rgb), 0.12)",
                          }}
                        >
                          <div
                            className="progress-bar shimmer-bar"
                            role="progressbar"
                            style={{
                              width: `${Math.min(100, Math.max(0, nextRewardProgress))}%`,
                              borderRadius: "10px",
                              backgroundImage:
                                "linear-gradient(90deg, #50a5f1 0%, var(--bs-primary) 100%)",
                            }}
                            aria-valuenow={nextRewardProgress}
                            aria-valuemin="0"
                            aria-valuemax="100"
                          ></div>
                        </div>
                        <span
                          className="fw-bold font-size-13 text-primary text-nowrap"
                          style={{ minWidth: "35px" }}
                        >
                          {nextRewardProgress}%
                        </span>
                      </div>

                      {/* Lots Traded and Lots To Go without broken wrapping */}
                      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 font-size-12 font-size-sm-13 pt-1">
                        <span
                          className="fw-semibold text-nowrap d-inline-flex align-items-center gap-1"
                          style={{ color: "var(--bs-secondary-color)" }}
                        >
                          <i className="bx bx-layer font-size-14 text-muted"></i>
                          <span>{nextRewardCurrentLots} / {nextRewardTargetLots} Lots</span>
                        </span>

                        <span
                          className="fw-bold d-inline-flex align-items-center gap-1 text-nowrap"
                          style={{ color: "#f1734f" }}
                        >
                          <span className="flame-animated">
                            <i className="bx bxs-hot font-size-15"></i>
                          </span>
                          <span>{nextRewardLotsToGo} lots to go!</span>
                        </span>
                      </div>
                    </div>
                  </div>

                  {isNextRewardUnlocked && (
                    <div className="flex-shrink-0 align-self-stretch align-self-md-center mt-2 mt-md-0 ps-md-2">
                      <button
                        type="button"
                        className="btn btn-primary fw-bold px-4 py-2 d-flex align-items-center justify-content-center gap-2 shadow-sm w-100 w-md-auto"
                        onClick={() => handleOpenClaimModal(nextRewardItem)}
                        style={{ borderRadius: "8px", fontSize: "14px", minWidth: "120px" }}
                      >
                        <i className="bx bx-gift font-size-17"></i>
                        <span>Claim</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <RewardsJourney
              rewards={rewardsData}
              onClaimReward={handleOpenClaimModal}
              onRefresh={fetchTradeEarnList}
            />
          </div>
        </div>

        <Footer />
      </div>

      <ClaimRewardModal
        isOpen={isClaimModalOpen}
        reward={selectedRewardToClaim}
        onClose={() => setIsClaimModalOpen(false)}
        onSuccess={fetchTradeEarnList}
      />
    </div>
  );
}
